import type React from "react";
import {memo, useCallback, useMemo} from "react";

import {
    ChevronLeftIcon,
    ChevronRightIcon,
    DoubleChevronLeftIcon,
    DoubleChevronRightIcon
} from "../../../react/components/Icons";
import buildTablePageNumbers from "../../../utils/buildTablePageNumbers";
import {Box} from "../box";
import {ButtonIcon, ButtonText} from "../button";
import {HStack} from "../hstack";
import {Text} from "../text";

import ButtonPage from "./ButtonPage";

interface Props {
    testID?: string;
    totalPages: number;
    currentPage: number;
    hasPrevPage: boolean;
    hasNextPage: boolean;
    className?: string;
    onPrevious: () => void;
    onNext: () => void;
    onChangePage: (page: number) => void;
}

const TablePagination: React.FC<Props> = ({
    testID,
    totalPages,
    currentPage,
    hasNextPage,
    hasPrevPage,
    className,
    onNext,
    onPrevious,
    onChangePage
}) => {
    const paginationNumbers = useMemo(
        () =>
            typeof totalPages === "number" ? buildTablePageNumbers(currentPage, totalPages) : [],
        [currentPage, totalPages]
    );

    const lastPage = paginationNumbers[paginationNumbers.length - 1];

    const goLast = useCallback(() => {
        if (typeof lastPage === "number") {
            onChangePage(lastPage - 1);
        }
    }, [lastPage, onChangePage]);

    const goFirst = useCallback(() => {
        onChangePage(0);
    }, [onChangePage]);

    return (
        <HStack className={className} space="sm" testID={testID}>
            <ButtonPage isDisabled={currentPage === 1} onPress={goFirst}>
                <ButtonIcon as={DoubleChevronLeftIcon} />
            </ButtonPage>
            <ButtonPage isDisabled={!hasPrevPage} onPress={onPrevious}>
                <ButtonIcon as={ChevronLeftIcon} />
            </ButtonPage>
            {paginationNumbers.map(page =>
                typeof page === "string" ? (
                    <Box key={page} className="w-8 h-8 content-end justify-center">
                        <Text textAlign="center" variant="xs">
                            ...
                        </Text>
                    </Box>
                ) : (
                    <ButtonPage
                        key={page}
                        isActive={page === currentPage}
                        onPress={() => onChangePage(page - 1)}
                    >
                        <ButtonText>{page}</ButtonText>
                    </ButtonPage>
                )
            )}
            <ButtonPage isDisabled={!hasNextPage} onPress={onNext}>
                <ButtonIcon as={ChevronRightIcon} />
            </ButtonPage>
            <ButtonPage isDisabled={currentPage === lastPage} onPress={goLast}>
                <ButtonIcon as={DoubleChevronRightIcon} />
            </ButtonPage>
        </HStack>
    );
};

export default memo(TablePagination);
